import { useEffect, useState } from "react";
import {
  Search,
  ChevronLeft,
  ChevronRight,
  FileText,
  Calendar,
  Building,
  Clock,
  Filter,
  CheckCircle,
  XCircle,
  AlertCircle,
  Eye,
  Edit2,
  Save,
  X,
  Trash2,
  Download,
  Paperclip,
  Package,
  DollarSign,
  MapPin,
  Printer,
  RotateCcw,
  Upload,
} from "lucide-react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import megalogo from '../Image/megalogo.png';
import PrPoTagsItem from "./PrPoTagsItem";
import Swal from 'sweetalert2';

import API_BASE_URL from "../config";
// Status mapping
const STATUS_MAP = {
  0: { label: "Draft", color: "gray", icon: AlertCircle },
  1: { label: "Pending", color: "yellow", icon: Clock },
  2: { label: "Approved", color: "green", icon: CheckCircle },
  3: { label: "Rejected", color: "red", icon: XCircle },
};

// PR Type mapping
const PR_TYPE_MAP = {
  0: "Regular",
  1: "Urgent",
  2: "Special",
};

export default function MyRequestsPage({ setCurrentPage }) {
  const [requests, setRequests] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [companies, setCompanies] = useState({});
  const [expenseTypes, setExpenseTypes] = useState({});
  const [paymentMethods, setPaymentMethods] = useState({});
  const [locations, setLocations] = useState({});
  const [departments, setDepartments] = useState({});
  const [showTagsSidebar, setShowTagsSidebar] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("dateNewest");
  const [destinations, setDestinations] = useState({});

  const [showItemModalInEdit, setShowItemModalInEdit] = useState(false);
  const [currentEditItemIndex, setCurrentEditItemIndex] = useState(null);
  const [editItemSearchQuery, setEditItemSearchQuery] = useState("");
  const [filteredEditItems, setFilteredEditItems] = useState([]);



  // ✅ ADD THIS sa top ng MyRequestsPage component (around line 61)
  const [itemsList, setItemsList] = useState([]);

  // ✅ ADD THIS useEffect (around line 90, after other useEffects)
  useEffect(() => {
    fetch(`${API_BASE_URL}api/Items`)
      .then(res => res.json())
      .then(data => {
        setItemsList(data);
        setFilteredEditItems(data);
      })
      .catch(err => console.error("Error fetching items:", err));
  }, []);
  // ✅ ADD THIS FUNCTION sa MyRequestsPage (before return statement)
  const handleEditItemSearch = (query) => {
    setEditItemSearchQuery(query);
    if (query.trim() === "") {
      setFilteredEditItems(itemsList);
    } else {
      const filtered = itemsList.filter(item =>
        item.itemDesciption?.toLowerCase().includes(query.toLowerCase()) ||
        item.itemCode?.toLowerCase().includes(query.toLowerCase())
      );
      setFilteredEditItems(filtered);
    }
  };

  const selectEditItem = (item, detailId) => {
    if (currentEditItemIndex !== null) {
      // Update the prDetails state
      const updatedDetails = [...prDetails];
      updatedDetails[currentEditItemIndex] = {
        ...updatedDetails[currentEditItemIndex],
        itemCode: item.itemCode || "",
        itemDescription: item.itemDesciption || "",
        uom: item.oumSystem || updatedDetails[currentEditItemIndex].uom
      };
      setPrDetails(updatedDetails);

      setShowItemModalInEdit(false);
      setCurrentEditItemIndex(null);
      setEditItemSearchQuery("");
    }
  };

  const fetchDestinations = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}api/InventoryDestination`);
      const data = await res.json();
      const destMap = {};
      data.forEach((d) => {
        destMap[d.destinationId] = d;
      });
      setDestinations(destMap);
    } catch (error) {
      console.error(error);
    }
  };
  // Modal state
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("view");
  const [prDetails, setPrDetails] = useState([]);
  const [prAttachments, setPrAttachments] = useState([]);
  const [editFormData, setEditFormData] = useState({});

  // Tooltip state
  const [tooltip, setTooltip] = useState({ show: false, text: "", x: 0, y: 0 });

  // Pagination
  const [page, setPage] = useState(1);
  const perPage = 10;
  const totalPages = Math.ceil(filtered.length / perPage);
  const paginated = filtered.slice((page - 1) * perPage, page * perPage);

  // ---------------- API FUNCTIONS ---------------- //

  const fetchCompanies = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}api/Company`);
      if (!res.ok) throw new Error("Failed to fetch companies");
      const data = await res.json();
      const companyMap = {};
      data.forEach((c) => {
        companyMap[c.id] = c.companyName || c.CompanyName || "Unknown";
      });
      setCompanies(companyMap);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchExpenseTypes = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}api/ExpenseType`);
      if (!res.ok) throw new Error("Failed to fetch expense types");
      const data = await res.json();
      const expenseMap = {};
      data.forEach((e) => {
        expenseMap[e.id] = e.expenseTypeName || e.ExpenseTypeName || "Unknown";
      });
      setExpenseTypes(expenseMap);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchPaymentMethods = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}api/M_Payment_Method`);  // CHANGED URL
      const data = await res.json();
      const paymentMap = {};
      data.forEach((p) => {
        paymentMap[p.id] = p.paymentMethodName || "Unknown";  // MATCH EXACT FIELD NAME
      });
      setPaymentMethods(paymentMap);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchLocations = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}api/Location`);
      const data = await res.json();
      const locationMap = {};
      data.forEach((l) => {
        locationMap[l.id] = l.locationName || "Unknown";
      });
      setLocations(locationMap);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchDepartments = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}api/Department`);
      const data = await res.json();
      const deptMap = {};
      data.forEach((d) => {
        deptMap[d.id] = d.departmentName || "Unknown";
      });
      setDepartments(deptMap);
    } catch (error) {
      console.error(error);
    }
  };

  // Sa fetchRequests function, add user filtering
  const fetchRequests = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}api/PR_Header`);
      if (!res.ok) throw new Error("Failed to fetch requests");

      const data = await res.json();

      if (!user || !userLevel) {
        setRequests([]);
        setFiltered([]);
        return;
      }

      let filteredData = [];

      if (userLevel.accessViewAllData === true) {
        filteredData = data;
      }
      else if (userLevel.accessVIewUserOnly === true) {
        filteredData = data.filter(
          req => req.createdBy === user.userId
        );
      }

      // ✅ SORT BY LATEST CREATION DATE
      filteredData.sort((a, b) => new Date(b.creationDate) - new Date(a.creationDate));

      setRequests(filteredData);
      setFiltered(filteredData);
    } catch (error) {
      console.error(error);
      alert("Failed to fetch requests");
    }
  };



  const [user, setUser] = useState(null);
  const [userLevel, setUserLevel] = useState(null);
  const [isSystemAdmin, setIsSystemAdmin] = useState(false);

  useEffect(() => {
    const fetchUserAndLevel = async () => {
      try {
        const storedUser = localStorage.getItem("user");
        if (!storedUser) return;

        const userData = JSON.parse(storedUser);
        setUser(userData);

        // ✅ CHECK IF USER IS SYSTEM ADMIN
        // ✅ CHECK IF USER IS SYSTEM ADMIN
        console.log("User Position:", userData.positionId); // ADD THIS TO DEBUG
        const isSysAdmin = userData.positionId === "SYSTEM ADMIN";
        console.log("Is System Admin:", isSysAdmin); // ADD THIS TO DEBUG
        setIsSystemAdmin(isSysAdmin);
        const res = await fetch(`${API_BASE_URL}api/UserLevels`);
        const levels = await res.json();

        const matchedLevel = levels.find(
          l =>
            l.isActive === 1 &&
            l.userLevelName === userData.positionId
        );

        setUserLevel(matchedLevel || null);
      } catch (err) {
        console.error("User/UserLevel error:", err);
      }
    };

    if (API_BASE_URL) {
      fetchUserAndLevel();
    }
  }, [API_BASE_URL]);


  const fetchPRDetails = async (prHeaderId) => {
    try {
      const res = await fetch(
        `${API_BASE_URL}api/PR_Details/PRHeader/${prHeaderId}`
      );
      const data = await res.json();

      // ✅ CHANGED: Show all PR Details to everyone (removed filtering)
      setPrDetails(data);
    } catch (error) {
      console.error(error);
      setPrDetails([]);
    }
  };

  useEffect(() => {
    if (user && userLevel) {
      fetchRequests();
    }
  }, [user, userLevel]);



  const fetchPRAttachments = async (prHeaderId) => {
    try {
      const res = await fetch(`${API_BASE_URL}api/PR_Attachments/PRHeader/${prHeaderId}`);
      const data = await res.json();
      setPrAttachments(data);
    } catch (error) {
      console.error(error);
      setPrAttachments([]);
    }
  };

  // ---------------- USE EFFECTS ---------------- //
  const getDestinationBadge = (destinationId) => {
    const dest = destinations[destinationId];
    if (!dest) return <span className="text-xs text-gray-500">—</span>;

    let type, color;
    if (dest.asset && !dest.wms && !dest.both) {
      type = 'Asset';
      color = 'bg-green-100 text-green-800';
    } else if (dest.wms && !dest.asset && !dest.both) {
      type = 'WMS';
      color = 'bg-blue-100 text-blue-800';
    } else if (dest.both) {
      type = 'Both';
      color = 'bg-purple-100 text-purple-800';
    } else {
      type = 'N/A';
      color = 'bg-gray-100 text-gray-800';
    }

    return (
      <div>
        <span className={`px-3 py-1 rounded-full text-xs font-bold ${color}`}>
          {type}
        </span>
        <p className="text-xs text-gray-600 mt-1">
          {dest.destinationName} ({dest.destinationCode})
        </p>
      </div>
    );
  };
  useEffect(() => {
    fetchCompanies();
    fetchExpenseTypes();
    fetchPaymentMethods();
    fetchLocations();
    fetchDepartments();
    fetchRequests();
    fetchDestinations(); // 👈 dagdag to
    fetchRequestors();  // 👈 ADD THIS


  }, []);

  useEffect(() => {
    let result = [...requests];

    if (search.trim()) {
      const term = search.toLowerCase();
      result = result.filter(
        (r) =>
          r.prNo?.toLowerCase().includes(term) ||
          r.remarks?.toLowerCase().includes(term) ||
          r.id?.toString().includes(term) ||
          companies[r.companyId]?.toLowerCase().includes(term) ||
          expenseTypes[r.expenseTypeId]?.toLowerCase().includes(term)
      );
    }

    if (statusFilter !== "all") {
      result = result.filter((r) => r.status === parseInt(statusFilter));
    }

    switch (sortBy) {
      case "dateNewest":
        result.sort((a, b) => new Date(b.creationDate) - new Date(a.creationDate));
        break;
      case "dateOldest":
        result.sort((a, b) => new Date(a.creationDate) - new Date(b.creationDate));
        break;
      case "prNoAsc":
        result.sort((a, b) => a.prNo.localeCompare(b.prNo));
        break;
      case "prNoDesc":
        result.sort((a, b) => b.prNo.localeCompare(a.prNo));
        break;
      case "statusAsc":
        result.sort((a, b) => a.status - b.status);
        break;
      default:
        break;
    }

    // ✅ ADD THIS - Default sort by latest if no sort selected
    if (!sortBy || sortBy === "dateNewest") {
      result.sort((a, b) => new Date(b.creationDate) - new Date(a.creationDate));
    }

    setFiltered(result);
    setPage(1);
  }, [search, statusFilter, sortBy, requests, companies, expenseTypes]);

  // ---------------- HELPERS ---------------- //

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatDateForInput = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  };



  const getStatusBadge = (status) => {
    const statusInfo = STATUS_MAP[status] || STATUS_MAP[0];
    const Icon = statusInfo.icon;

    const colorClasses = {
      gray: "bg-gray-100 text-gray-800",
      yellow: "bg-yellow-100 text-yellow-800",
      green: "bg-green-100 text-green-800",
      red: "bg-red-100 text-red-800",
    };


    return (
      <span
        className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${colorClasses[statusInfo.color]
          }`}
      >
        <Icon size={14} />
        {statusInfo.label}
      </span>
    );
  };

  const truncateText = (text, maxLength = 30) => {
    if (!text) return "—";
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  const handleMouseEnter = (e, text) => {
    if (!text || text.length <= 30) return;
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltip({
      show: true,
      text: text,
      x: rect.left + rect.width / 2,
      y: rect.top - 10,
    });
  };

  const handleMouseLeave = () => {
    setTooltip({ show: false, text: "", x: 0, y: 0 });
  };

  const handleViewDetails = async (request) => {
    setSelectedRequest(request);
    setModalMode("view");
    await fetchPRDetails(request.id);
    await fetchPRAttachments(request.id);
    setShowModal(true);

  };

  const handleEditRequest = async (request) => {
    setSelectedRequest(request);
    setModalMode("edit");
    await fetchPRDetails(request.id);
    await fetchPRAttachments(request.id);
    setEditFormData({
      ...request,
      creationDate: formatDateForInput(request.creationDate),
      dateNeeded: formatDateForInput(request.dateNeeded),
      dateSubmittedForApproval: formatDateForInput(request.dateSubmittedForApproval),
    });
    setShowModal(true);
  };

  const [requestors, setRequestors] = useState([]);
  const fetchRequestors = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}api/Requestor`);
      const data = await res.json();
      setRequestors(data);
    } catch (error) {
      console.error(error);
    }
  };
  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedRequest(null);
    setModalMode("view");
    setPrDetails([]);
    setPrAttachments([]);
    setEditFormData({});
  };
  const handleDeleteRequest = async (requestId) => {
    // Create custom confirmation modal
    const confirmed = await new Promise((resolve) => {
      const modal = document.createElement('div');
      modal.innerHTML = `
        <div style="position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 9999;">
          <div style="background: white; padding: 32px; border-radius: 16px; max-width: 400px; text-align: center; box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1);">
            <div style="width: 64px; height: 64px; background: #FEE2E2; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto 16px;">
              <svg style="width: 32px; height: 32px; color: #EF4444;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
              </svg>
            </div>
            <h3 style="font-size: 24px; font-weight: bold; color: #1F2937; margin-bottom: 8px;">Are you sure?</h3>
            <p style="color: #6B7280; margin-bottom: 24px;">This will permanently delete the PR Header, all Items, and all Attachments!</p>
            <div style="display: flex; gap: 12px; justify-content: center;">
              <button id="cancel-btn" style="padding: 12px 24px; background: #E5E7EB; color: #374151; border: none; border-radius: 8px; font-weight: 600; cursor: pointer;">Cancel</button>
              <button id="confirm-btn" style="padding: 12px 24px; background: #EF4444; color: white; border: none; border-radius: 8px; font-weight: 600; cursor: pointer;">Yes, delete it!</button>
            </div>
          </div>
        </div>
      `;

      document.body.appendChild(modal);

      modal.querySelector('#cancel-btn').onclick = () => {
        document.body.removeChild(modal);
        resolve(false);
      };

      modal.querySelector('#confirm-btn').onclick = () => {
        document.body.removeChild(modal);
        resolve(true);
      };
    });

    if (!confirmed) return;



    try {
      // Step 1: Delete all PR Details
      const detailsResponse = await fetch(`${API_BASE_URL}api/PR_Details/PRHeader/${requestId}`, {
        method: 'DELETE',
      });

      if (!detailsResponse.ok) {
        console.warn('Failed to delete PR Details, continuing...');
      }

      // Step 2: Delete all PR Attachments
      const attachmentsResponse = await fetch(`${API_BASE_URL}api/PR_Attachments/PRHeader/${requestId}`, {
        method: 'DELETE',
      });

      if (!attachmentsResponse.ok) {
        console.warn('Failed to delete PR Attachments, continuing...');
      }

      // Step 3: Delete PR Header
      const headerResponse = await fetch(`${API_BASE_URL}api/PR_Header/${requestId}`, {
        method: 'DELETE',
      });

      if (headerResponse.ok || headerResponse.status === 204) {
        const user = JSON.parse(localStorage.getItem("user"));
        await fetch(`${API_BASE_URL}api/Logs`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            action: "DELETE",
            module: "Purchase Request",
            description: `PR ID: ${requestId} was permanently deleted`,
            userId: user?.userId,
            username: user?.username,
            fullName: user?.fullName || user?.username,
          })
        }).catch(err => console.error("Log failed:", err));
        // 🔴 HANGGANG DITO
        await fetchRequests();

        // SUCCESS MODAL
        const successModal = document.createElement('div');
        successModal.innerHTML = `
          <div style="position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 9999;">
            <div style="background: white; padding: 32px; border-radius: 16px; max-width: 400px; text-align: center; box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1);">
              <div style="width: 64px; height: 64px; background: #D1FAE5; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto 16px;">
                <svg style="width: 32px; height: 32px; color: #10B981;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                </svg>
              </div>
              <h3 style="font-size: 24px; font-weight: bold; color: #1F2937; margin-bottom: 8px;">Deleted!</h3>
              <p style="color: #6B7280; margin-bottom: 24px;">PR has been deleted successfully.</p>
              <button id="ok-btn" style="padding: 12px 24px; background: #10B981; color: white; border: none; border-radius: 8px; font-weight: 600; cursor: pointer; width: 100%;">OK</button>
            </div>
          </div>
        `;

        document.body.appendChild(successModal);

        successModal.querySelector('#ok-btn').onclick = () => {
          document.body.removeChild(successModal);
        };

        // Auto close after 2 seconds
        setTimeout(() => {
          if (document.body.contains(successModal)) {
            document.body.removeChild(successModal);
          }
        }, 2000);

      } else {
        throw new Error('Failed to delete PR Header');
      }
    } catch (error) {
      console.error('Error deleting PR:', error);

      // ERROR MODAL
      const errorModal = document.createElement('div');
      errorModal.innerHTML = `
        <div style="position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 9999;">
          <div style="background: white; padding: 32px; border-radius: 16px; max-width: 400px; text-align: center; box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1);">
            <div style="width: 64px; height: 64px; background: #FEE2E2; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto 16px;">
              <svg style="width: 32px; height: 32px; color: #EF4444;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </div>
            <h3 style="font-size: 24px; font-weight: bold; color: #1F2937; margin-bottom: 8px;">Error!</h3>
            <p style="color: #6B7280; margin-bottom: 24px;">Failed to delete PR. Some data may still exist.</p>
            <button id="ok-btn" style="padding: 12px 24px; background: #EF4444; color: white; border: none; border-radius: 8px; font-weight: 600; cursor: pointer; width: 100%;">OK</button>
          </div>
        </div>
      `;

      document.body.appendChild(errorModal);

      errorModal.querySelector('#ok-btn').onclick = () => {
        document.body.removeChild(errorModal);
      };
    }
  };

  const handleSavePRHeader = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}api/PR_Header/${selectedRequest.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...editFormData,
          id: selectedRequest.id,
        })
      });


      if (response.ok) {

        const user = JSON.parse(localStorage.getItem("user"));
        await fetch(`${API_BASE_URL}api/Logs`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            action: "UPDATE",
            module: "Purchase Request",
            description: `PR ${selectedRequest.prNo} was updated. PR ID: ${selectedRequest.id}`,
            userId: user?.userId,
            username: user?.username,
            fullName: user?.fullName || user?.username,
          })
        }).catch(err => console.error("Log failed:", err));
        // 🔴 HANGGANG DITO LANG

        await fetchRequests();
        handleCloseModal();


        // SUCCESS MODAL
        const successModal = document.createElement('div');
        successModal.innerHTML = `
          <div style="position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 9999;">
            <div style="background: white; padding: 32px; border-radius: 16px; max-width: 400px; text-align: center; box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1);">
              <div style="width: 64px; height: 64px; background: #D1FAE5; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto 16px;">
                <svg style="width: 32px; height: 32px; color: #10B981;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                </svg>
              </div>
              <h3 style="font-size: 24px; font-weight: bold; color: #1F2937; margin-bottom: 8px;">Success!</h3>
              <p style="color: #6B7280; margin-bottom: 24px;">PR Header updated successfully!</p>
              <button id="ok-btn" style="padding: 12px 24px; background: #10B981; color: white; border: none; border-radius: 8px; font-weight: 600; cursor: pointer; width: 100%;">OK</button>
            </div>
          </div>
        `;

        document.body.appendChild(successModal);

        successModal.querySelector('#ok-btn').onclick = () => {
          document.body.removeChild(successModal);
        };

        // Auto close after 2 seconds
        setTimeout(() => {
          if (document.body.contains(successModal)) {
            document.body.removeChild(successModal);
          }
        }, 2000);

      } else {
        // FAILED MODAL
        const failModal = document.createElement('div');
        failModal.innerHTML = `
          <div style="position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 9999;">
            <div style="background: white; padding: 32px; border-radius: 16px; max-width: 400px; text-align: center; box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1);">
              <div style="width: 64px; height: 64px; background: #FEE2E2; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto 16px;">
                <svg style="width: 32px; height: 32px; color: #EF4444;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </div>
              <h3 style="font-size: 24px; font-weight: bold; color: #1F2937; margin-bottom: 8px;">Failed!</h3>
              <p style="color: #6B7280; margin-bottom: 24px;">Failed to update PR Header</p>
              <button id="ok-btn" style="padding: 12px 24px; background: #EF4444; color: white; border: none; border-radius: 8px; font-weight: 600; cursor: pointer; width: 100%;">OK</button>
            </div>
          </div>
        `;

        document.body.appendChild(failModal);

        failModal.querySelector('#ok-btn').onclick = () => {
          document.body.removeChild(failModal);
        };
      }
    } catch (error) {
      console.error('Error updating PR Header:', error);

      // ERROR MODAL
      const errorModal = document.createElement('div');
      errorModal.innerHTML = `
        <div style="position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 9999;">
          <div style="background: white; padding: 32px; border-radius: 16px; max-width: 400px; text-align: center; box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1);">
            <div style="width: 64px; height: 64px; background: #FEE2E2; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto 16px;">
              <svg style="width: 32px; height: 32px; color: #EF4444;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
              </svg>
            </div>
            <h3 style="font-size: 24px; font-weight: bold; color: #1F2937; margin-bottom: 8px;">Error!</h3>
            <p style="color: #6B7280; margin-bottom: 24px;">Error updating PR Header</p>
            <button id="ok-btn" style="padding: 12px 24px; background: #EF4444; color: white; border: none; border-radius: 8px; font-weight: 600; cursor: pointer; width: 100%;">OK</button>
          </div>
        </div>
      `;

      document.body.appendChild(errorModal);

      errorModal.querySelector('#ok-btn').onclick = () => {
        document.body.removeChild(errorModal);
      };
    }
  };

  const handleUpdateDetail = async (detailId, updatedData) => {
    try {
      const response = await fetch(`${API_BASE_URL}api/PR_Details/${detailId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...updatedData,
          id: detailId,
        })
      });

      if (response.ok) {
        await fetchPRDetails(selectedRequest.id);
        alert('Detail updated successfully!');
      } else {
        alert('Failed to update detail');
      }
    } catch (error) {
      console.error('Error updating detail:', error);
      alert('Error updating detail');
    }
  };

  const handleDeleteDetail = async (detailId) => {

    try {
      const response = await fetch(`${API_BASE_URL}api/PR_Details/${detailId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        await fetchPRDetails(selectedRequest.id);
        alert('Detail deleted successfully!');
      } else {
        alert('Failed to delete detail');
      }
    } catch (error) {
      console.error('Error deleting detail:', error);
      alert('Error deleting detail');
    }
  };

  const handleDownloadAttachment = async (attachment) => {
    try {
      const response = await fetch(`${API_BASE_URL}api/PR_Attachments/${attachment.id}`);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = attachment.filename || 'download';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading attachment:', error);
      alert('Failed to download attachment');
    }
  };

  // ✅ HELPER: Check if edit/delete buttons should show
  // ✅ HELPER: Check if edit/delete buttons should show
  // ✅ HELPER: Check if edit/delete buttons should show
  // ✅ HELPER: Check if edit/delete buttons should show
const canEditDelete = (request) => {
  const isOwner = user && request.createdBy === user.userId;
  const canManage = isSystemAdmin || isOwner;

  if (!canManage) return false;

  // ✅ SYSTEM ADMIN can edit/delete even if Approved
  if (isSystemAdmin) return true;

  // ❌ Regular users cannot edit/delete if Approved (status 2)
  if (request.status === 2) return false;

  return true;
};
  // ---------------- RETURN UI ---------------- //
  return (
    <div className="min-h-screen  from-slate-50 via-blue-50 to-indigo-100 p-8">
      {/* BACK BUTTON */}
      <button
        onClick={() => setCurrentPage && setCurrentPage("dashboard")}
        className="mb-2 flex items-center gap-2 text-blue-600 hover:text-blue-800 font-semibold transition-colors group ml-8"
      >
        <ChevronLeft
          size={20}
          className="group-hover:-translate-x-1 transition-transform"
        />
        Back to Dashboard
      </button>

      {/* HEADER */}
      <div className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100 mb-8">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl shadow-lg">
              <FileText className="text-white" size={32} />
            </div>
            <div>
              <h1 className="text-4xl font-black bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 bg-clip-text text-transparent">
                My Requests
              </h1>
              <p className="text-gray-500 text-sm mt-1">View and manage your purchase requisitions</p>
            </div>
          </div>
          <div className="text-right">
            <div className="text-4xl font-black text-blue-600">{filtered.length}</div>
            <div className="text-sm text-gray-500">Total Requests</div>
          </div>
        </div>
      </div>

      {/* FILTERS & SEARCH */}
      <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="relative col-span-1 md:col-span-1">
          <Search
            size={20}
            className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400"
          />
          <input
            type="text"
            placeholder="Search by PR No, Company..."
            className="w-full pl-12 pr-4 py-3 bg-white rounded-xl border-2 border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all shadow-sm"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="relative">
          <Filter
            size={20}
            className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full pl-12 pr-4 py-3 bg-white rounded-xl border-2 border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all shadow-sm appearance-none cursor-pointer"
          >
            <option value="all">All Status</option>
            <option value="0">Draft</option>
            <option value="1">Pending</option>
            <option value="2">Approved</option>
            <option value="3">Rejected</option>
          </select>
        </div>

        <div className="relative">
          <Clock
            size={20}
            className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"
          />
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="w-full pl-12 pr-4 py-3 bg-white rounded-xl border-2 border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all shadow-sm appearance-none cursor-pointer"
          >
            <option value="dateNewest">Date: Newest First</option>
            <option value="dateOldest">Date: Oldest First</option>
            <option value="prNoAsc">PR No: A-Z</option>
            <option value="prNoDesc">PR No: Z-A</option>
            <option value="statusAsc">Status: Low to High</option>
          </select>
        </div>
      </div>

      {/* TABLE CARD */}
      {/* TABLE - SIMPLIFIED VERSION */}
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
                <th className="px-6 py-4 text-left text-sm font-semibold">PR No</th>
                <th className="px-6 py-4 text-left text-sm font-semibold">Requestor</th>
                <th className="px-6 py-4 text-left text-sm font-semibold">Department</th>
                <th className="px-6 py-4 text-left text-sm font-semibold">Created Date</th>

                <th className="px-6 py-4 text-left text-sm font-semibold">Date Needed</th>
                <th className="px-6 py-4 text-left text-sm font-semibold">Status</th>
                <th className="px-6 py-4 text-center text-sm font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {paginated.length > 0 ? (
                paginated.map((request, index) => (
                  <tr
                    key={request.id || `request-${index}`}
                    className="hover:bg-blue-50 transition-colors"
                  >
                    {/* PR No */}
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-sm">
                          {request.id}
                        </div>
                        <div>
                          <div className="font-semibold text-gray-800">{request.prNo}</div>
                          <div className="text-xs text-gray-500">
                            {request.controlNumber || "No Control #"}
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* Requestor */}
                    <td className="px-6 py-4">
                      <span className="text-sm font-medium text-gray-700">
                        {request.requestor || "—"}
                      </span>
                    </td>

                    {/* Department */}
                    <td className="px-6 py-4">
                      <span className="text-sm font-medium text-gray-700">
                        {request.department || "—"}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 text-sm text-gray-700">
                        <Calendar size={16} className="text-gray-400" />
                        {formatDate(request.creationDate)}
                      </div>
                    </td>
                    {/* Date Needed */}
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 text-sm text-gray-700">
                        <Clock size={16} className="text-gray-400" />
                        {formatDate(request.dateNeeded)}
                      </div>
                    </td>

                    {/* Status */}
                    <td className="px-6 py-4">{getStatusBadge(request.status)}</td>

                    {/* Actions */}
                    <td className="px-6 py-4">
                      <div className="flex justify-center gap-2">
                        <button
                          onClick={() => {
                            handleViewDetails(request);   // fetch prDetails
                            setShowTagsSidebar(true);     // open sidebar
                            setSelectedRequest(request);
                          }}
                          className="p-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-all shadow-sm hover:shadow-md hover:scale-110"
                          title="View Details"
                        >
                          <Eye size={16} />
                        </button>

                        {/* ✅ ONLY SHOW EDIT/DELETE IF ALLOWED */}
                        {canEditDelete(request) && (
                          <>
                            <button
                              onClick={() => handleEditRequest(request)}
                              className="p-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-all shadow-sm hover:shadow-md hover:scale-110"
                              title="Edit Request"
                            >
                              <Edit2 size={16} />
                            </button>
                            <button
                              onClick={() => handleDeleteRequest(request.id)}
                              className="p-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-all shadow-sm hover:shadow-md hover:scale-110"
                              title="Delete Request"
                            >
                              <Trash2 size={16} />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="px-6 py-16 text-center">
                    <div className="flex flex-col items-center gap-3 text-gray-400">
                      <FileText size={48} />
                      <p className="text-lg font-medium">No requests found</p>
                      <p className="text-sm">Try adjusting your search or filters</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* PAGINATION - same lang */}
        {paginated.length > 0 && (
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">
                Showing {(page - 1) * perPage + 1} to{" "}
                {Math.min(page * perPage, filtered.length)} of {filtered.length} requests
              </span>
              <div className="flex items-center gap-3">
                <button
                  disabled={page <= 1}
                  onClick={() => setPage(page - 1)}
                  className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm"
                >
                  <ChevronLeft size={18} />
                </button>
                <span className="text-sm font-semibold text-gray-700">
                  Page {page} of {totalPages || 1}
                </span>
                <button
                  disabled={page >= totalPages}
                  onClick={() => setPage(page + 1)}
                  className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm"
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* TOOLTIP */}
      {
        tooltip.show && (
          <div
            className="fixed z-50 bg-gray-900 text-white text-sm px-4 py-2 rounded-lg shadow-xl max-w-xs pointer-events-none"
            style={{
              left: `${tooltip.x}px`,
              top: `${tooltip.y}px`,
              transform: "translate(-50%, -100%)",
            }}
          >
            {tooltip.text}
            <div
              className="absolute w-2 h-2 bg-gray-900 transform rotate-45"
              style={{
                left: "50%",
                bottom: "-4px",
                marginLeft: "-4px",
              }}
            />
          </div>
        )
      }

      {/* SUMMARY CARDS */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
        {Object.entries(STATUS_MAP).map(([key, value]) => {
          const count = requests.filter((r) => r.status === parseInt(key)).length;
          const Icon = value.icon;
          return (
            <div
              key={key}
              className="bg-white rounded-xl shadow-lg p-6 border-l-4"
              style={{
                borderLeftColor:
                  value.color === "gray"
                    ? "#6B7280"
                    : value.color === "yellow"
                      ? "#F59E0B"
                      : value.color === "green"
                        ? "#10B981"
                        : "#EF4444",
              }}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">{value.label}</p>
                  <p className="text-2xl font-bold text-gray-800 mt-1">{count}</p>
                </div>
                <Icon size={32} className="text-gray-400" />
              </div>
            </div>
          );
        })}
      </div>

      {/* MODAL */}
      {
        showModal && selectedRequest && (
          <RequestDetailsModal
            request={selectedRequest}
            mode={modalMode}
            prDetails={prDetails}
            prAttachments={prAttachments}
            editFormData={editFormData}
            setEditFormData={setEditFormData}
            companies={companies}
            expenseTypes={expenseTypes}
            paymentMethods={paymentMethods}
            locations={locations}
            departments={departments}
            onClose={handleCloseModal}
            onSave={handleSavePRHeader}
            onUpdateDetail={handleUpdateDetail}
            onDeleteDetail={handleDeleteDetail}
            onDownloadAttachment={handleDownloadAttachment}
            setModalMode={setModalMode}
            formatDate={formatDate}
            requestors={requestors}
            isSystemAdmin={isSystemAdmin}
            // ✅ ADD THESE NEW PROPS
            showItemModalInEdit={showItemModalInEdit}
            setShowItemModalInEdit={setShowItemModalInEdit}
            currentEditItemIndex={currentEditItemIndex}
            setCurrentEditItemIndex={setCurrentEditItemIndex}
            editItemSearchQuery={editItemSearchQuery}
            handleEditItemSearch={handleEditItemSearch}
            filteredEditItems={filteredEditItems}
            selectEditItem={selectEditItem}
          />
        )
      }
      {
        showTagsSidebar && selectedRequest && (
          <PrPoTagsItem
            request={selectedRequest}
            prDetails={prDetails}
            companies={companies}
            expenseTypes={expenseTypes}
            locations={locations}
            departments={departments}
            onClose={() => setShowTagsSidebar(false)}
          />
        )
      }
    </div >
  );
}

// Separate Modal Component
// ✅ SA RequestDetailsModal component (around line 1155)
// I-UPDATE MO YUNG RETURN STATEMENT NG RequestDetailsModal

function RequestDetailsModal({
  request,
  mode,
  prDetails,
  prAttachments,
  editFormData,
  setEditFormData,
  companies,
  expenseTypes,
  paymentMethods,
  locations,
  departments,
  onClose,
  onSave,
  onUpdateDetail,
  onDeleteDetail,
  onDownloadAttachment,
  setModalMode,
  formatDate,
  requestors,
  isSystemAdmin,
  // ✅ ADD THESE PROPS HERE
  showItemModalInEdit,
  setShowItemModalInEdit,
  currentEditItemIndex,
  setCurrentEditItemIndex,
  editItemSearchQuery,
  handleEditItemSearch,
  filteredEditItems,
  selectEditItem
}) {
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className=" rounded-3xl shadow-2xl w-full max-w-7xl my-8">
        {/* Modal Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 text-white rounded-t-3xl">
          {/* ... header code ... */}
        </div>

        {/* Modal Body */}
        <div className="p-6 max-h-[80vh] overflow-y-auto bg-gray-50">
          {mode === 'view' ? (
            <ViewMode
              request={request}
              prDetails={prDetails}
              prAttachments={prAttachments}
              companies={companies}
              expenseTypes={expenseTypes}
              paymentMethods={paymentMethods}
              locations={locations}
              departments={departments}
              formatDate={formatDate}
              onDownloadAttachment={onDownloadAttachment}
              API_BASE_URL={API_BASE_URL}
              requestors={requestors}
              onClose={onClose} />
          ) : (
            <EditMode
              request={request}
              prDetails={prDetails}
              prAttachments={prAttachments}
              editFormData={editFormData}
              setEditFormData={setEditFormData}
              companies={companies}
              expenseTypes={expenseTypes}
              paymentMethods={paymentMethods}
              locations={locations}
              departments={departments}
              onSave={onSave}
              onUpdateDetail={onUpdateDetail}
              onDeleteDetail={onDeleteDetail}
              onDownloadAttachment={onDownloadAttachment}
              setModalMode={setModalMode}
              isSystemAdmin={isSystemAdmin}
              // ✅ PASS ALL THESE PROPS TO EditMode
              showItemModalInEdit={showItemModalInEdit}
              setShowItemModalInEdit={setShowItemModalInEdit}
              currentEditItemIndex={currentEditItemIndex}
              setCurrentEditItemIndex={setCurrentEditItemIndex}
              editItemSearchQuery={editItemSearchQuery}
              handleEditItemSearch={handleEditItemSearch}
              filteredEditItems={filteredEditItems}
              selectEditItem={selectEditItem}
            />
          )}
        </div>
      </div>
    </div>
  );
}


function ViewMode({
  request,
  prDetails,
  prAttachments,
  companies,
  expenseTypes,
  paymentMethods,
  locations,
  departments,
  formatDate,
  onDownloadAttachment,
  requestors,
  onClose,
  API_BASE_URL


}) {
  const [websiteInfo, setWebsiteInfo] = useState(null);
  const [customLogo, setCustomLogo] = useState(null);
  const [showLogoUploader, setShowLogoUploader] = useState(false);
  const [approverPositions, setApproverPositions] = useState([]);
  const [approvals, setApprovals] = useState([]);
  const [users, setUsers] = useState([]);


  useEffect(() => {
    if (API_BASE_URL && request?.id) {
      fetchSecondApprovals(request.id);
    }
  }, [API_BASE_URL, request?.id]);

  useEffect(() => {
    const fetchApprovals = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}api/T_PR_Approval`);
        if (!res.ok) throw new Error("Failed to fetch approvals");
        const data = await res.json();

        const prApprovals = data.filter(
          a =>
            a.prHeaderId === request.id &&
            a.response === 1 &&
            a.isActive === 1
        );

        setApprovals(prApprovals);
      } catch (err) {
        console.error("PR Approval error:", err);
      }
    };

    if (API_BASE_URL && request?.id) {
      fetchApprovals();
    }
  }, [API_BASE_URL, request?.id]);
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}api/Users`);
        if (!res.ok) throw new Error("Failed to fetch users");
        const data = await res.json();

        setUsers(data.filter(u => u.isActive === 1));
      } catch (err) {
        console.error("Users error:", err);
      }
    };

    if (API_BASE_URL) {
      fetchUsers();
    }
  }, [API_BASE_URL]);
  const getApprovedUser = (approverUserLevelId) => {
    const approval = approvals.find(
      a => a.approverUserLevelId === approverUserLevelId
    );

    if (!approval) return null;

    const user = users.find(
      u => u.id === approval.actualApproverId
    );

    const signatureUrl = approval.actualApproverId
      ? `${API_BASE_URL}api/Users/${approval.actualApproverId}/digitalsign`
      : null;

    return {
      name: user
        ? `${user.firstName} ${user.lastName}`
        : "—",
      date: approval.responseDate,
      signatureUrl
    };
  };


  useEffect(() => {
    const fetchApproverPositions = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}api/M_Approver_Position`);
        if (!res.ok) throw new Error("Failed to fetch approver positions");
        const data = await res.json();

        // PR only + sort by column
        const prApprovers = data
          .filter(a => a.pr === 1)
          .sort((a, b) => a.colum - b.colum);

        setApproverPositions(prApprovers);
      } catch (err) {
        console.error("Approver Position error:", err);
      }
    };

    if (API_BASE_URL) {
      fetchApproverPositions();
    }
  }, [API_BASE_URL]);

  // Fetch active website info
  useEffect(() => {
    const fetchWebsiteInfo = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}api/Website_Details`);
        if (!res.ok) throw new Error('Failed to fetch website info');
        const data = await res.json();

        const activeWebsite = data.find(w => w.isActive) || data[0];
        if (activeWebsite) {
          setWebsiteInfo(activeWebsite);
        }
      } catch (error) {
        console.error('Error fetching website info:', error);
      }
    };

    if (API_BASE_URL) {
      fetchWebsiteInfo();
    }
  }, [API_BASE_URL]);

  // Handle logo upload
  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      alert('File size must be less than 2MB');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result;
      setCustomLogo(base64String);
      setShowLogoUploader(false);
    };
    reader.readAsDataURL(file);
  };

  // Reset to default logo
  const handleResetLogo = () => {
    setCustomLogo(null);
    setShowLogoUploader(false);
  };

  // Determine which logo to show
  const getLogoSrc = () => {
    if (customLogo) return customLogo;
    // Always show megalogo as default
    return megalogo; // or '/megalogo.png' kung nasa public folder
  };

  // Export to PDF function
  const handleExportToPDF = async () => {
    const element = document.getElementById("pr-pdf-content");

    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
      windowHeight: element.scrollHeight,
      height: element.scrollHeight
    });

    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "mm", "a4");

    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = pdfWidth;
    const imgHeight = (canvas.height * pdfWidth) / canvas.width;

    let heightLeft = imgHeight;
    let position = 0;

    // Add first page
    pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
    heightLeft -= pdfHeight;

    // Add additional pages if content is longer
    while (heightLeft > 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pdfHeight;
    }

    pdf.save(`PR-${request.prNo}.pdf`);
  };

  const logoSrc = getLogoSrc();


  const [secondApprovals, setSecondApprovals] = useState([]);
  const fetchSecondApprovals = async (prHeaderId) => {
    try {
      const res = await fetch(`${API_BASE_URL}api/SecondApproverPR`);
      if (!res.ok) throw new Error("Failed to fetch second approvals");
      const data = await res.json();
      const prSecondApprovals = data.filter(
        a => a.prHeaderId === prHeaderId && a.response === 1
      );
      setSecondApprovals(prSecondApprovals);
    } catch (err) {
      console.error("Second approval error:", err);
    }
  };
  return (
    <div className="p-6 font-sans space-y-6">
      <div className="no-print flex justify-between items-center mb-4">
        {/* Left — Close */}
        <button
          onClick={onClose}
          className="flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 px-5 py-3 rounded-xl font-bold transition-all"
        >
          <X size={20} />
          Close
        </button>

        {/* Right — Print + PDF */}
        <div className="flex gap-3">
          <button
            onClick={() => window.print()}
            className="flex items-center gap-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white px-6 py-3 rounded-xl font-bold hover:shadow-lg transition-all"
          >
            <Printer size={20} />
            Print
          </button>
          <button
            onClick={handleExportToPDF}
            className="flex items-center gap-2 bg-gradient-to-r from-green-500 to-green-600 text-white px-6 py-3 rounded-xl font-bold hover:shadow-lg transition-all"
          >
            <Download size={20} />
            PDF
          </button>
        </div>
      </div>

      {/* Wrap entire content for PDF export */}
      <div id="pr-pdf-content" className="bg-white p-8">
        {/* Header with Logo, Company Info, and PR Details */}
        <div className="border-b-2 border-gray-400 pb-6 mb-6">
          {/* Top Section: Logo + Company Info (Centered) */}
          <div className="text-center mb-4 relative">
            {/* Logo with Upload Option */}
            <div className="relative inline-block group">
              {logoSrc ? (
                <div className="flex justify-center mb-5">
                  <img
                    src={logoSrc}
                    alt="Company Logo"
                    className="h-32 w-auto object-contain max-w-sm"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />

                  {/* Hover overlay - Hide on print */}
                  <div className="no-print absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-2">
                    <button
                      onClick={() => setShowLogoUploader(true)}
                      className="p-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-all"
                      title="Change Logo"
                    >
                      <Upload size={16} />
                    </button>
                    {customLogo && (
                      <button
                        onClick={handleResetLogo}
                        className="p-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition-all"
                        title="Reset to Default"
                      >
                        <RotateCcw size={16} />
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <div
                  onClick={() => setShowLogoUploader(true)}
                  className="no-print flex justify-center mb-3 cursor-pointer"
                >
                  <div className="h-20 w-32 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center hover:border-blue-500 hover:bg-blue-50 transition-all">
                    <div className="text-center">
                      <Upload size={24} className="mx-auto text-gray-400 mb-1" />
                      <p className="text-xs text-gray-500">Upload Logo</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Slogan */}
            {websiteInfo?.websiteSlogan && (
              <p className="text-sm text-gray-600 italic mb-2">
                {websiteInfo.websiteSlogan}
              </p>
            )}

            {/* Address */}
            {websiteInfo?.websiteAddress && (
              <p className="text-sm text-gray-700 mb-1">
                {websiteInfo.websiteAddress}
              </p>
            )}

            {/* Contact Number */}
            {websiteInfo?.websiteContact && (
              <p className="text-sm text-gray-700 mb-1">
                {websiteInfo.websiteContact}
              </p>
            )}
          </div>

          {/* Document Title and PR Info */}
          <div className="flex justify-between items-center mt-4 pt-4 border-t border-gray-300">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">Purchase Requisition </h2>
              <p className="text-base text-gray-600">PR No: <span className="font-semibold">{request.prNo || "—"}</span></p>
            </div>
            <div className="text-right text-base">
              <div className="text-gray-600">Date Printed:</div>
              <div className="font-semibold">{formatDate(new Date())}</div>
            </div>
          </div>
        </div>

        {/* Request Information */}
        <div className="mb-6">
          {/* Header with Control Number */}
          <div className="flex justify-between items-center mb-3 border-b border-gray-300 pb-2">
            <h3 className="text-xl font-semibold text-gray-800">
              Request Information
            </h3>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-500">Control Number:</span>
              <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-lg font-mono text-sm font-semibold">
                {request.controlNumber || 'N/A'}
              </span>
            </div>
          </div>

          {/* Grid - ONLY fields that exist in SQL */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-base">
            {[
              ["Requestor", request.requestor || "—"],
              ["Date Needed", formatDate(request.dateNeeded)],
              ["Date Submitted", formatDate(request.dateSubmittedForApproval)],
              ["Priority", request.priority || "—"],
            ].map(([label, value]) => (
              <div key={label} className="flex gap-2">
                <span className="font-semibold text-gray-700 min-w-[160px]">{label}:</span>
                <span className="text-gray-600 break-words">{value}</span>
              </div>
            ))}
          </div>
        </div>


        {/* Remarks */}
        {request.remarks && (
          <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-md text-amber-900 text-base mb-6">
            <strong className="block mb-1">Remarks:</strong>
            <span>{request.remarks}</span>
          </div>
        )}

        {/* PR Details Table */}
        <div className="mb-6">
          <h3 className="text-xl font-semibold text-gray-800 mb-3 border-b border-gray-300 pb-2">
            Items Requested ({prDetails.length})
          </h3>
          <div className="overflow-x-auto shadow-md rounded-lg">
            <table className="w-full border-collapse border border-gray-300 text-base">
              <thead className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
                <tr>
                  {["#", "Item Description", "Specification", "Qty", "UOM", "Remarks"].map((header) => (
                    <th key={header} className="border border-blue-500 px-3 py-3 text-left font-semibold">
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {prDetails.length > 0 ? (
                  prDetails.map((d, idx) => (
                    <tr key={d.id} className={`${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                      <td className="border border-gray-300 px-3 py-2 text-center font-medium text-gray-600">{idx + 1}</td>
                      <td className="border border-gray-300 px-3 py-2 break-words">{d.itemDescription || "—"}</td>
                      <td className="border border-gray-300 px-3 py-2 break-words">{d.materialFinish || "—"}</td>
                      <td className="border border-gray-300 px-3 py-2 text-center font-semibold">{d.qty}</td>
                      <td className="border border-gray-300 px-3 py-2 text-center">{d.uom || "—"}</td>
                      <td className="border border-gray-300 px-3 py-2 break-words text-sm text-gray-600">{d.remarks || "—"}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="px-3 py-8 text-center text-gray-500">
                      <div className="flex flex-col items-center gap-2">
                        <span className="text-2xl">📋</span>
                        <span>No items found</span>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Attachments */}
        {prAttachments.length > 0 && (
          <div className="mb-6">
            <h3 className="text-xl font-semibold text-gray-800 mb-3 border-b border-gray-300 pb-2">
              Attachments ({prAttachments.length})
            </h3>
            <div className="bg-gray-50 rounded-lg p-4">
              <ul className="space-y-2">
                {prAttachments.map(att => (
                  <li key={att.id} className="flex justify-between items-center bg-white p-3 rounded-md shadow-sm border border-gray-200">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <span className="text-2xl">📎</span>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-800 break-words">{att.filename}</div>
                        <div className="text-sm text-gray-500">
                          {att.contentType}
                          {att.description && ` • ${att.description}`}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => onDownloadAttachment(att)}
                      className="no-print ml-3 px-4 py-2 bg-blue-500 text-white rounded-md text-sm font-medium hover:bg-blue-600 transition-colors flex items-center gap-2 whitespace-nowrap"
                    >
                      <span>📥</span>
                      Download
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}



        <div className="mt-6 pt-4 border-t border-gray-400">
          {/* Signatures */}
          <div className="grid grid-cols-3 gap-10 text-center">

            {/* REQUESTOR (Form Creator) */}
            {/* REQUESTOR (Created By) */}
            {/* REQUESTOR */}
            {(() => {
              const requestorName = request?.requestor || `User ${request?.createdBy || "-"}`;

              // Find matching requestor by name
              const requestorData = requestors.find(r => r.name === requestorName);

              const signatureUrl = requestorData?.signature
                ? `${API_BASE_URL}api/Requestor/${requestorData.id}/signature`
                : null;

              return (
                <div className="flex flex-col items-center">
                  <div className="h-14 flex items-center justify-center">
                    {signatureUrl && (
                      <img
                        src={signatureUrl}
                        alt="Requestor signature"
                        className="h-12 max-w-[140px] object-contain"
                        crossOrigin="anonymous"
                        onError={(e) => (e.currentTarget.style.display = "none")}
                      />
                    )}
                  </div>

                  <div className="text-sm font-semibold leading-tight">
                    {requestorName}
                  </div>

                  <div className="w-full border-b border-gray-500 my-1" />

                  <div className="text-xs text-gray-500 leading-tight">
                    {request?.creationDate
                      ? new Date(request.creationDate).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })
                      : ""}
                  </div>
                </div>
              );
            })()}


            {/* ENDORSEMENT (Level 2) */}
            {(() => {
              const endorsement = secondApprovals.find(a => a.approvalLevel === 2);
              return (
                <div className="flex flex-col items-center">
                  <div className="h-14 flex items-center justify-center">
                    {endorsement?.actualApproverId && (
                      <img
                        src={`${API_BASE_URL}api/Users/${endorsement.actualApproverId}/digitalsign`}
                        alt="Endorsement signature"
                        className="h-12 max-w-[140px] object-contain"
                        crossOrigin="anonymous"
                        onError={(e) => (e.currentTarget.style.display = "none")}
                      />
                    )}
                  </div>

                  <div className="text-sm font-semibold leading-tight">
                    {endorsement?.actualApproverName || "-"}
                  </div>

                  <div className="w-full border-b border-gray-500 my-1" />

                  <div className="text-xs text-gray-500 leading-tight">
                    {endorsement?.responseDate
                      ? new Date(endorsement.responseDate).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })
                      : ""}
                  </div>
                </div>
              );
            })()}

            {/* ACKNOWLEDGEMENT (Level 3) */}
            {(() => {
              const acknowledgement = secondApprovals.find(a => a.approvalLevel === 3);
              return (
                <div className="flex flex-col items-center">
                  <div className="h-14 flex items-center justify-center">
                    {acknowledgement?.actualApproverId && (
                      <img
                        src={`${API_BASE_URL}api/Users/${acknowledgement.actualApproverId}/digitalsign`}
                        alt="Acknowledgement signature"
                        className="h-12 max-w-[140px] object-contain"
                        crossOrigin="anonymous"
                        onError={(e) => (e.currentTarget.style.display = "none")}
                      />
                    )}
                  </div>

                  <div className="text-sm font-semibold leading-tight">
                    {acknowledgement?.actualApproverName || "-"}
                  </div>

                  <div className="w-full border-b border-gray-500 my-1" />

                  <div className="text-xs text-gray-500 leading-tight">
                    {acknowledgement?.responseDate
                      ? new Date(acknowledgement.responseDate).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })
                      : ""}
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Signature label */}
          <div className="grid grid-cols-3 gap-10 text-center text-xs text-gray-600 mt-2">
            <div>Signature over Printed Name</div>
            <div>Signature over Printed Name</div>
            <div>Signature over Printed Name</div>
          </div>

          {/* Position titles */}
          <div className="grid grid-cols-3 gap-10 text-center text-sm font-bold mt-1">
            <div className="uppercase">REQUESTOR</div>
            <div className="uppercase">ENDORSEMENT</div>
            <div className="uppercase">ACKNOWLEDGEMENT</div>
          </div>
        </div>

        <div className="mt-8 pt-6 border-t-2 border-gray-400">
          <div className="grid grid-cols-3 gap-8 text-center text-base">
            {approverPositions.map((approver) => {
              const approved = getApprovedUser(approver.approverUserLevelId);

              return (
                <div key={approver.id} className="flex flex-col items-center">

                  {/* SIGNATURE SLOT (FIXED HEIGHT) */}
                  <div className="h-20 flex items-center justify-center mb-1">
                    {approved?.signatureUrl ? (
                      <img
                        src={approved.signatureUrl}
                        alt={`${approved.name} signature`}
                        className="h-16 object-contain"
                        crossOrigin="anonymous"
                        onError={(e) => { e.target.style.display = "none"; }}
                      />
                    ) : (
                      <span className="text-transparent">no-sign</span> // keeps spacing
                    )}
                  </div>
                  <div className="font-semibold">
                    {approved?.name || "—"}
                  </div>

                  {/* LINE */}
                  <div className="w-full border-b border-gray-400 mb-"></div>

                  {/* NAME */}


                  {/* POSITION */}
                  <div className="text-sm text-gray-700 mb-1">
                    {approver.approverPosition}
                  </div>

                  {/* LABEL */}
                  <div className="text-sm text-gray-600 mt-1">
                    Signature over Printed Name
                  </div>
                </div>
              );
            })}
          </div>
        </div>






      </div>

      {/* Logo Uploader Modal */}
      {showLogoUploader && (
        <div className="no-print fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-800">Upload Custom Logo</h3>
              <button
                onClick={() => setShowLogoUploader(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div
                className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-blue-500 hover:bg-blue-50 transition-all cursor-pointer"
                onClick={() => document.getElementById('logoInput').click()}
              >
                <Upload size={48} className="mx-auto text-gray-400 mb-3" />
                <p className="text-gray-700 font-medium mb-1">Click to upload logo</p>
                <p className="text-sm text-gray-500">PNG, JPG up to 2MB</p>
              </div>

              <input
                type="file"
                id="logoInput"
                accept="image/*"
                onChange={handleLogoUpload}
                className="hidden"
              />

              {customLogo && (
                <div className="flex gap-2">
                  <button
                    onClick={handleResetLogo}
                    className="flex-1 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium transition-colors"
                  >
                    Reset to Default
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Print Styles */}
      <style jsx>{`
            @media print {
              .no-print {
                display: none !important;
              }
              
              body {
                print-color-adjust: exact;
                -webkit-print-color-adjust: exact;
              }
              
              @page {
                margin: 1cm;
                size: A4;
              }
            }
          `}</style>
    </div>
  );
}




// Edit Mode Component
// Edit Mode Component - ✅ SIMPLIFIED, ALIGNED WITH CREATE FORM
// ✅ COMPLETE EditMode COMPONENT WITH PAGINATION
function EditMode({
  request,
  prDetails,
  prAttachments,
  editFormData,
  setEditFormData,
  onSave,
  onUpdateDetail,
  onDeleteDetail,
  onDownloadAttachment,
  setModalMode,
  isSystemAdmin,
  showItemModalInEdit,
  setShowItemModalInEdit,
  currentEditItemIndex,
  setCurrentEditItemIndex,
  editItemSearchQuery,
  handleEditItemSearch,
  filteredEditItems,
  selectEditItem
}) {
  const [inventoryDestinations, setInventoryDestinations] = useState([]);
  const [itemsList, setItemsList] = useState([]);

  // ✅ ADD PAGINATION STATE
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;

  useEffect(() => {
    fetch(`${API_BASE_URL}api/InventoryDestination`)
      .then(res => res.json())
      .then(data => setInventoryDestinations(data))
      .catch(err => console.error("Error loading inventory destinations:", err));

    fetch(`${API_BASE_URL}api/Items`)
      .then(res => res.json())
      .then(data => setItemsList(data))
      .catch(err => console.error("Error fetching items:", err));
  }, []);

  return (
    <div className="space-y-6">
      {/* ✅ ITEM SEARCH MODAL WITH PAGINATION */}
      {showItemModalInEdit && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-5xl w-full max-h-[80vh] overflow-hidden">
            {/* HEADER */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-4 flex justify-between items-center">
              <h3 className="text-xl font-bold flex items-center gap-2">
                <Search size={20} />
                Search Items
              </h3>
              <button
                onClick={() => {
                  setShowItemModalInEdit(false);
                  setCurrentEditItemIndex(null);
                  handleEditItemSearch("");
                  setCurrentPage(1); // ✅ Reset page
                }}
                className="text-white hover:bg-white/20 rounded-lg p-2 transition-all">
                <X size={20} />
              </button>
            </div>

            {/* SEARCH INPUT */}
            <div className="p-6">
              <div className="mb-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
                  <input
                    type="text"
                    placeholder="Search by item description or code..."
                    value={editItemSearchQuery}
                    onChange={(e) => {
                      handleEditItemSearch(e.target.value);
                      setCurrentPage(1); // ✅ Reset to page 1 on search
                    }}
                    className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    autoFocus
                  />
                </div>
              </div>

              {/* ITEMS TABLE WITH PAGINATION */}
              <div className="overflow-y-auto max-h-[50vh] border border-slate-200 rounded-lg">
                {filteredEditItems.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <Search size={48} className="mx-auto mb-4 opacity-50" />
                    <p className="text-lg font-semibold">No items found</p>
                  </div>
                ) : (
                  <>
                    <table className="w-full">
                      <thead className="bg-slate-50 sticky top-0">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Item Code</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Description</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Category</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">OUM System</th>
                        </tr>
                      </thead>
                      <tbody>
                        {/* ✅ SHOW ONLY CURRENT PAGE ITEMS */}
                        {filteredEditItems
                          .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                          .map((item) => (
                            <tr
                              key={item.id}
                              onClick={() => {
                                selectEditItem(item);
                                setCurrentPage(1); // ✅ Reset after selection
                              }}
                              className="border-b border-slate-100 hover:bg-blue-50 cursor-pointer transition-colors">
                              <td className="px-4 py-3 text-sm font-medium text-slate-800">{item.itemCode}</td>
                              <td className="px-4 py-3 text-sm text-slate-700">{item.itemDesciption}</td>
                              <td className="px-4 py-3 text-sm text-slate-600">
                                <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                                  {item.category || 'N/A'}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-sm text-slate-600">
                                <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-medium">
                                  {item.oumSystem || 'N/A'}
                                </span>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>

                    {/* ✅ PAGINATION CONTROLS */}
                    <div className="sticky bottom-0 bg-white border-t border-slate-200 px-4 py-3 flex items-center justify-between">
                      {/* Results count */}
                      <div className="text-sm text-slate-600">
                        Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredEditItems.length)} of {filteredEditItems.length} items
                      </div>

                      {/* Pagination buttons */}
                      <div className="flex gap-2">
                        <button
                          onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                          disabled={currentPage === 1}
                          className="px-3 py-1 border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium">
                          Previous
                        </button>

                        {/* Page numbers */}
                        <div className="flex items-center gap-1">
                          {Array.from({ length: Math.min(5, Math.ceil(filteredEditItems.length / itemsPerPage)) }, (_, i) => {
                            const totalPages = Math.ceil(filteredEditItems.length / itemsPerPage);
                            let pageNum;

                            if (totalPages <= 5) {
                              pageNum = i + 1;
                            } else if (currentPage <= 3) {
                              pageNum = i + 1;
                            } else if (currentPage >= totalPages - 2) {
                              pageNum = totalPages - 4 + i;
                            } else {
                              pageNum = currentPage - 2 + i;
                            }

                            return (
                              <button
                                key={pageNum}
                                onClick={() => setCurrentPage(pageNum)}
                                className={`w-8 h-8 rounded text-sm font-medium ${currentPage === pageNum
                                  ? 'bg-blue-600 text-white'
                                  : 'border border-slate-300 hover:bg-slate-50'
                                  }`}>
                                {pageNum}
                              </button>
                            );
                          })}
                        </div>

                        <button
                          onClick={() => setCurrentPage(p => Math.min(Math.ceil(filteredEditItems.length / itemsPerPage), p + 1))}
                          disabled={currentPage >= Math.ceil(filteredEditItems.length / itemsPerPage)}
                          className="px-3 py-1 border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium">
                          Next
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* PR HEADER EDIT */}
      <div>
        <h3 className="text-lg font-bold text-gray-800 mb-4">Edit Request Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <InputField label="PR No" value={editFormData.prNo} disabled onChange={(v) => setEditFormData({ ...editFormData, prNo: v })} />
          <InputField label="Date Needed" type="date" value={editFormData.dateNeeded} onChange={(v) => setEditFormData({ ...editFormData, dateNeeded: v })} />

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Type of Purchase</label>
            <select
              value={editFormData.typeOfPurchase || "Item"}
              onChange={(e) => setEditFormData({ ...editFormData, typeOfPurchase: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 cursor-pointer"
            >
              <option value="Item">Item</option>
              <option value="Service">Service</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Priority</label>
            <select
              value={editFormData.priority || ''}
              onChange={(e) => setEditFormData({ ...editFormData, priority: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 cursor-pointer"
            >
              <option value="">Select...</option>
              <option value="Emergency Order">Emergency Order</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Inventory Destination</label>
            <select
              value={editFormData.inventoryDestinationId || ''}
              onChange={(e) => setEditFormData({ ...editFormData, inventoryDestinationId: parseInt(e.target.value) })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 cursor-pointer"
            >
              <option value="">Select...</option>
              {inventoryDestinations.filter(dest => dest.isActive).map((dest) => (
                <option key={dest.destinationId} value={dest.destinationId}>
                  {dest.destinationCode} - {dest.destinationName}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Type</label>
            <select
              value={editFormData.type || ''}
              onChange={(e) => setEditFormData({ ...editFormData, type: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 cursor-pointer"
            >
              <option value="">Select...</option>
              <option value="Local">Local</option>
              <option value="Import">Import</option>
            </select>
          </div>

          <InputField label="Requestor" value={editFormData.requestor || request.requestor} disabled onChange={(v) => { }} />
          <InputField label="Department" value={editFormData.department || request.department} disabled onChange={(v) => { }} />

          <div className="md:col-span-3">
            <label className="block text-sm font-semibold text-gray-700 mb-2">Remarks</label>
            <textarea
              value={editFormData.remarks || ''}
              onChange={(e) => setEditFormData({ ...editFormData, remarks: e.target.value })}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      {/* ✅ EDIT PR DETAILS WITH SEARCH BUTTON */}
      <div>
        <h3 className="text-lg font-bold text-gray-800 mb-4">Edit Items</h3>
        <div className="space-y-4">
          {prDetails.map((detail, idx) => (
            <DetailEditCard
              key={detail.id}
              detail={detail}
              detailIndex={idx}
              onUpdate={onUpdateDetail}
              onDelete={onDeleteDetail}
              isSystemAdmin={isSystemAdmin}
              onSearchClick={(index) => {
                setCurrentEditItemIndex(index);
                setShowItemModalInEdit(true);
                handleEditItemSearch("");
                setCurrentPage(1); // ✅ Reset page when opening
              }}
            />
          ))}
        </div>
      </div>

      {/* ATTACHMENTS */}
      {prAttachments.length > 0 && (
        <div>
          <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
            <Paperclip size={20} />
            Attachments
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {prAttachments.map((attachment) => (
              <div key={attachment.id} className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 flex-1">
                    <Paperclip size={18} className="text-blue-600" />
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-800 truncate text-sm">{attachment.filename}</p>
                      <p className="text-xs text-gray-600">{attachment.description}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => onDownloadAttachment(attachment)}
                    className="ml-2 p-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-all"
                  >
                    <Download size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* SAVE BUTTONS */}
      <div className="flex gap-3 pt-4 border-t">
        <button
          onClick={onSave}
          className="flex items-center gap-2 bg-gradient-to-r from-green-500 to-green-600 text-white px-6 py-3 rounded-xl font-bold hover:shadow-lg transition-all"
        >
          <Save size={20} />
          Save Changes
        </button>
        <button
          onClick={() => setModalMode('view')}
          className="flex items-center gap-2 bg-gray-200 text-gray-700 px-6 py-3 rounded-xl font-bold hover:bg-gray-300 transition-all"
        >
          <X size={20} />
          Cancel
        </button>
      </div>
    </div>
  );
}

// Helper Components
function InfoCard({ icon: Icon, label, value }) {
  return (
    <div className="bg-gradient-to-br from-gray-50 to-blue-50 p-4 rounded-xl border border-gray-200">
      <div className="flex items-start gap-3">
        {Icon && (
          <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0">
            <Icon size={18} className="text-blue-600" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className="text-xs text-gray-500 mb-1">{label}</p>
          <p className="font-semibold text-gray-800 truncate">{value || "—"}</p>
        </div>
      </div>
    </div>
  );
}

function InputField({ label, type = "text", value, onChange, disabled = false }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-gray-700 mb-2">{label}</label>
      <input
        type={type}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
      />
    </div>
  );
}

function SelectField({ label, value, onChange, options }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-gray-700 mb-2">{label}</label>
      <select
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
      >
        <option value="">Select {label}</option>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    </div>
  );
}

// Detail Edit Card Component - ✅ WITH isSystemAdmin CHECK
// Detail Edit Card Component - ✅ ALLOW EDITING OF ITEM DETAILS
function DetailEditCard({ detail, detailIndex, onUpdate, onDelete, isSystemAdmin, onSearchClick }) {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState(detail);

  const handleSave = () => {
    onUpdate(detail.id, formData);
    setIsEditing(false);
  };

  return (
    <div className="bg-gradient-to-r from-gray-50 to-blue-50 p-4 rounded-xl border border-gray-200">
      {isEditing ? (
        <div className="space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {/* ✅ ITEM CODE WITH SEARCH */}
            <div className="relative">
              <input
                type="text"
                placeholder="Item Code"
                value={formData.itemCode || ''}
                onChange={(e) => setFormData({ ...formData, itemCode: e.target.value })}
                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg text-sm"
              />
              <button
                onClick={() => onSearchClick(detailIndex)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded p-1.5 transition-all"
                title="Search items"
              >
                <Search size={16} />
              </button>
            </div>

            {/* ✅ ITEM DESCRIPTION WITH SEARCH */}
            <div className="relative md:col-span-2">
              <input
                type="text"
                placeholder="Item Description"
                value={formData.itemDescription || ''}
                onChange={(e) => setFormData({ ...formData, itemDescription: e.target.value })}
                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg text-sm"
              />
              <button
                onClick={() => onSearchClick(detailIndex)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded p-1.5 transition-all"
                title="Search items"
              >
                <Search size={16} />
              </button>
            </div>
                 <input
              type="text"
              placeholder="Specification"
              value={formData.materialFinish || ''}
              onChange={(e) => setFormData({ ...formData, materialFinish: e.target.value })}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />

            <input
              type="number"
              placeholder="Qty"
              value={formData.qty || ''}
              onChange={(e) => setFormData({ ...formData, qty: parseFloat(e.target.value) })}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
            
            <input
              type="text"
              placeholder="UOM"
              value={formData.uom || ''}
              onChange={(e) => setFormData({ ...formData, uom: e.target.value })}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
       
        
            <input
              type="text"
              placeholder="Remarks"
              value={formData.remarks || ''}
              onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
              className="md:col-span-2 px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleSave}
              className="flex items-center gap-1 bg-green-500 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-green-600"
            >
              <Save size={16} />
              Save
            </button>
            <button
              onClick={() => setIsEditing(false)}
              className="flex items-center gap-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm font-semibold hover:bg-gray-400"
            >
              <X size={16} />
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="flex justify-between items-center">
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4 flex-1">
            <div>
              <p className="text-xs text-gray-500">Item Code</p>
              <p className="text-sm font-semibold">{detail.itemCode || '—'}</p>
            </div>
            <div className="md:col-span-2">
              <p className="text-xs text-gray-500">Item</p>
              <p className="text-sm font-semibold">{detail.itemDescription}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Specification</p>
              <p className="text-sm font-semibold">{detail.materialFinish || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Qty</p>
              <p className="text-sm font-semibold">{detail.qty} {detail.uom}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Remarks</p>
              <p className="text-sm font-semibold">{detail.remarks || '—'}</p>
            </div>
          </div>

          <div className="flex gap-2 ml-4">
            <button
              onClick={() => setIsEditing(true)}
              className="p-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
            >
              <Edit2 size={16} />
            </button>
            {isSystemAdmin && (
              <button
                onClick={() => onDelete(detail.id)}
                className="p-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
              >
                <Trash2 size={16} />
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}